import { useMemo, useState, useSyncExternalStore } from "react";
import { useQuery } from "@tanstack/react-query";
import { Box, Card, CardContent, CircularProgress, Dialog, DialogContent, DialogTitle, Link, List, Typography } from "@mui/material";
import { lcFirst } from "../utils";
import {
    eventExtended,
    fetchEventsByHandles,
    personExtended,
    restPlaceName,
    toGrampsNote,
    type RestEvent,
    type RestPerson,
} from "../api/grampsRest";
import { restPersonName, useAncestorPersons } from "../hooks/useNotableEvents";
import { readBearerToken, subscribeToBearerToken } from "../grampsAuth";
import { useViewRoute } from "../viewRoute";
import { NoteItem } from "../components/NoteItem";

function restDateLabel(date: RestEvent['date'] | undefined): string {
    if (!date) return '?';
    if (date.text) return date.text;
    if (date.year) return String(date.year);
    return '?';
}

function personOccupationEvents(person: RestPerson): RestEvent[] {
    return (personExtended(person).events ?? []).filter((e) => e.type === 'Occupation' && e.description);
}

function personBirthEvent(person: RestPerson) {
    return (personExtended(person).events ?? []).find((e) => e.type === 'Birth');
}

function personDeathEvent(person: RestPerson) {
    return (personExtended(person).events ?? []).find((e) => e.type === 'Death');
}

export function Occupations() {
    const { mainId, treeName, setSideItemId } = useViewRoute();
    const token = useSyncExternalStore(subscribeToBearerToken, () => readBearerToken(treeName ?? null)) ?? undefined;

    const { data: people, isLoading } = useAncestorPersons(mainId ?? null, treeName ?? null);

    const mainPerson = useMemo(() => people?.find((p) => p.gramps_id === mainId), [people, mainId]);

    const personOccupations = useMemo(() => {
        return (people ?? [])
            .map<[RestPerson, RestEvent[]]>((p) => [p, personOccupationEvents(p)])
            .filter(([, events]) => events.length > 0)
            .sort(([a], [b]) => (personBirthEvent(b)?.date?.year ?? -Infinity) - (personBirthEvent(a)?.date?.year ?? -Infinity));
    }, [people]);

    // Occupation events carry only note handles until fetched with `note_list,place`
    // extended, so noted events are batch-fetched separately, same as NotableEvents.
    const notedEventHandles = useMemo(() => {
        const handles = new Set<string>();
        personOccupations.forEach(([, events]) => {
            events.forEach((e) => {
                if (e.handle && (e.note_list?.length ?? 0) > 0) handles.add(e.handle);
            });
        });
        return [...handles].sort();
    }, [personOccupations]);

    const notedEventsQuery = useQuery({
        queryKey: ['events', treeName, 'batch', notedEventHandles],
        queryFn: () => fetchEventsByHandles(notedEventHandles, token),
        enabled: notedEventHandles.length > 0,
    });

    const notedEventsByHandle = useMemo(() => {
        const map = new Map<string, RestEvent>();
        for (const event of notedEventsQuery.data ?? []) {
            if (event.handle) {
                map.set(event.handle, event);
            }
        }
        return map;
    }, [notedEventsQuery.data]);

    const [selectedHandle, setSelectedHandle] = useState<string | null>(null);
    const selectedEvent = selectedHandle ? notedEventsByHandle.get(selectedHandle) : undefined;
    const selectedNotes = selectedEvent ? eventExtended(selectedEvent).notes ?? [] : [];
    const selectedPlace = selectedEvent ? restPlaceName(eventExtended(selectedEvent).place) : undefined;

    if (!mainId) {
        return null;
    }

    const handleClick = (id: string) => {
        setSideItemId(id);
    };

    return (<Box>
        <Typography variant="h6" gutterBottom>
            Professions rencontrées parmi les ascendants de {mainPerson && restPersonName(mainPerson)}
        </Typography>
        {isLoading && personOccupations.length === 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} />
            </Box>
        )}
        {/* As many columns as the room left beside the drawer allows: the
            viewport alone says nothing of it, and six columns of a few letters
            each were unreadable. */}
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 170px), 1fr))', alignItems: 'start' }}>
            {personOccupations.map(([p, events], idx) => (
                <Box key={idx}>
                    <Card variant="outlined">
                        <CardContent>
                            <Link style={{ "cursor": "pointer" }} onClick={() => p.gramps_id && handleClick(p.gramps_id)}>{restPersonName(p)} </Link>
                            ({restDateLabel(personBirthEvent(p)?.date)} - {restDateLabel(personDeathEvent(p)?.date)}) était<br />

                            <Typography variant="overline" component="span">
                                {events.map((e, i) => {
                                    const label = lcFirst(e.description);
                                    const hasNotes = !!e.handle && notedEventsByHandle.has(e.handle);
                                    return (
                                        <span key={e.handle ?? i}>
                                            {i > 0 && ', '}
                                            {hasNotes ? (
                                                <Link sx={{ cursor: 'pointer' }} onClick={() => setSelectedHandle(e.handle!)}>
                                                    {label}
                                                </Link>
                                            ) : (
                                                label
                                            )}
                                        </span>
                                    );
                                })}
                            </Typography>
                        </CardContent>
                    </Card>
                </Box>
            ))}
        </Box>
        <Dialog open={!!selectedHandle} onClose={() => setSelectedHandle(null)} maxWidth="sm" fullWidth>
            <DialogTitle>
                {selectedEvent ? lcFirst(selectedEvent.description) : ''}
                {selectedEvent?.date && restDateLabel(selectedEvent.date) !== '?' ? ` — ${restDateLabel(selectedEvent.date)}` : ''}
                {selectedPlace ? ` (${selectedPlace})` : ''}
            </DialogTitle>
            <DialogContent dividers>
                {!selectedEvent && notedEventsQuery.isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : (
                    <List disablePadding>
                        {selectedNotes.map((note) => (
                            <NoteItem key={note.handle} note={toGrampsNote(note)} />
                        ))}
                    </List>
                )}
            </DialogContent>
        </Dialog>
    </Box>)
}
